import { NgIf } from '@angular/common';
import { Component, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SetupUserPasswordDto } from '@buspro/web/apis';
import { TenantUsersApi } from '@buspro/web/apis';
import { WEB_CONFIG, WebAppEnvironment, syncAutofilledInputs } from '@buspro/web/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'tenant-register',
  templateUrl: './setup-password.component.html',
  styleUrls: ['./setup-password.component.scss'],
  imports: [NgIf, NzAlertComponent, ReactiveFormsModule, RouterLink],
  standalone: true,
})
export class SetupPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);

  isSubmitting = signal<boolean>(false);
  isPasswordVisible = signal<boolean>(false);
  isPasswordConfirmVisible = signal<boolean>(false);
  form = this.fb.group({
    email: [{ value: '', disabled: true }, [Validators.required]],
    password: ['', [Validators.required]],
    passwordConfirm: ['', [Validators.required]],
  });

  private accountFormRef = viewChild<ElementRef<HTMLFormElement>>('accountForm');

  userId!: string;
  name = '';

  websiteUrl = this.environment.publicWebsiteUrl;

  constructor(
    private route: ActivatedRoute,
    private notification: NzNotificationService,
    private tenantUsersApi: TenantUsersApi,
  ) {}

  ngOnInit(): void {
    const { uid, name, email } = this.route.snapshot.queryParams;

    if (!uid) {
      this.notification.error('Ops! Something went wrong', 'Please contact support');
      return;
    }

    this.userId = uid;
    this.form.get('email')?.setValue(email);
    this.name = name ?? '';
  }

  completeAccountSetup() {
    // Adopt anything the browser filled in — a value assigned straight onto an
    // input never reaches the control, so the checks below would otherwise
    // reject a form whose fields are visibly complete.
    syncAutofilledInputs(this.form, this.accountFormRef()?.nativeElement);

    // The submit button used to be disabled while the form was invalid, which
    // told the user nothing about why. It is now always live, so this guard is
    // what stops an incomplete submit reaching the server.
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notification.error('Password required', 'Please fill in both password fields to continue');
      return;
    }

    if (this.form.get('password')?.value !== this.form.get('passwordConfirm')?.value) {
      this.notification.error('Passwords does not match', 'Please check your password');
      return;
    }

    const payload: SetupUserPasswordDto = {
      userId: this.userId,
      password: this.form.get('password')!.value!,
    };

    this.isSubmitting.set(true);
    this.tenantUsersApi
      .tenantUsersControllerCompleteUserAccountSetup({
        body: payload,
      })
      .subscribe({
        next: () => {
          this.router.navigate(['/passport/setup-success'], {
            queryParams: { name: this.name },
            replaceUrl: true,
          });
        },
        error: (error) => {
          console.log(error);
          this.notification.error('Ops! Something went wrong', error.error?.errorMessage);
          this.isSubmitting.set(false);
        },
      });
  }
}
